/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { Injectable, inject } from '@angular/core';
import {
  Action,
  Actions,
  Selector,
  State,
  StateContext,
  ofActionDispatched
} from '@ngxs/store';
import {
  Observable,
  catchError,
  defer,
  finalize,
  from,
  EMPTY,
  of,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { httpErrorToFormGroupError } from '@veone/ngx-utilities/form';
import * as immutable from 'object-path-immutable';
import { cloneDeep } from 'lodash-es';
import dayjs from 'dayjs';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import {
  AccessToken,
  AuthResponse,
  Challenge,
  LoginParams,
  Otp,
  RefreshToken,
  RegistrationParams,
  TokensResponse,
  User,
  UserCreationParams,
  UserRole
} from '~core/models/auth.models';
import {
  OtpData,
  StateFormModel,
  StateStepper
} from '~core/models/shared.models';
import { AuthStateModel } from '~backoffice/app/~shared/models/store.models';
import { AuthenticationService } from '~backoffice/app/~shared/services/authentication.service';
import {
  ChangeUserPassword,
  GetRegistrationOneTimePassword,
  GetResetOneTimePassword,
  GetUserProfile,
  GoToNextRegistrationStep,
  GoToPreviousRegistrationStep,
  GoToRegistrationStep,
  HydrateLoggedUserProfile,
  IdentifyUserCompany,
  LoginWithOneTimePassword,
  LoginWithPassword,
  LoginWithSocialNetwork,
  Logout,
  NavigateToLastRequestedRoute,
  PatchLoggedUser,
  PatchUserExistenceStatus,
  RefreshAccessToken,
  RegisterUser,
  ResetLoginState,
  ResetOnboardingForms,
  ResetRegisterState,
  ResetUserPassword,
  SetLastRequestedRoute
} from '~backoffice/app/~store/actions/auth.actions';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { setRememberMeStatus } from '~backoffice/app/~shared/utils/storage';
import { SetRouteData } from '~backoffice/app/~store/actions/global.actions';
import { EventService } from '~backoffice/app/~shared/services/event.service';
import {
  getNamesFromFullName,
  removeNilProperties
} from '~backoffice/app/~shared/utils/utils';
import { ProfileService } from '~core/services/profile.service';
import { ToastService } from '~backoffice/app/~shared/services/toast.service';
import { StepperState } from './stepper.state';
import { ROUTES } from '~backoffice/app/~shared/constants/routes.constants';
import { CompanyService } from '~core/services/company.service';
import { Company } from '~core/models/company.models';
import { AccountService } from '~backoffice/app/~shared/services/account.service';
import { LoggerService } from '~core/services/logger.service';
import {
  BillingEquipment,
  OrganisationFormValues
} from '~core/models/organization.models';
import { AUTH_STATE_TOKEN } from '~backoffice/app/~shared/constants/global.constants';
import { DialogService } from '~backoffice/app/~shared/services/dialog.service';

export const DEFAULT_AUTH_STATE: AuthStateModel = {
  currentUser: undefined,
  identifiedCompany: undefined,
  tokens: undefined,
  login: {
    loginFormValues: {
      model: {
        username: '',
        password: '',
        keepSession: false
      },
      dirty: false,
      status: '',
      errors: undefined,
      httpErrors: undefined
    },
    currentUserData: undefined,
    challenge: undefined,
    otp: undefined
  },
  registration: {
    registrationFormValues: {
      model: {
        email: '',
        fullName: '',
        phoneNumber: ''
      },
      dirty: false,
      status: '',
      errors: undefined,
      httpErrors: undefined
    },
    emailConfirmationFormValues: {
      model: {
        password: ''
      },
      dirty: false,
      status: '',
      errors: undefined,
      httpErrors: undefined
    },
    registeredUserData: undefined,
    otp: undefined,
    stepper: {
      currentStep: 0,
      totalSteps: 3
    },
    isUserKnown: undefined
  },
  onboarding: {
    locationFormValues: {
      model: {
        siteOwnerName: '',
        siteOwnerNif: '',
        siteOwnerPhoneNumber: '',
        siteOwnerEmail: '',
        email: '',
        phoneNumber: '',
        rccNumber: '',
        country: '',
        city: '',
        district: '',
        neighborhood: '',
        uniqueIdentifier: '',
        location: '',
        longitude: '',
        latitude: '',
        section: '',
        parcel: '',
        lot: '',
        ilot: ''
      },
      dirty: false,
      status: '',
      errors: undefined,
      httpErrors: undefined
    },
    billingEquipmentFormValues: {
      model: {
        equipments: null,
        cashRegistersNumber: null
      },
      dirty: false,
      status: '',
      errors: undefined,
      httpErrors: undefined
    }
  },
  reset: {
    requestFormValues: {
      model: {
        email: ''
      },
      dirty: false,
      status: '',
      errors: undefined,
      httpErrors: undefined
    },
    resetFormValues: {
      model: {
        token: '',
        password: ''
      },
      dirty: false,
      status: '',
      errors: undefined,
      httpErrors: undefined
    },
    resetUserData: undefined,
    otp: undefined
  },
  lastRequestedRoute: undefined
};

@State({
  name: AUTH_STATE_TOKEN,
  defaults: DEFAULT_AUTH_STATE
})
@Injectable()
export class AuthState {
  private _globalFacade = inject(GlobalFacade);
  private _authService = inject(AuthenticationService);
  private _companyService = inject(CompanyService);
  private _profileService = inject(ProfileService);
  private _accountService = inject(AccountService);
  private _eventService = inject(EventService);
  private _toastService = inject(ToastService);
  private _dialogService = inject(DialogService);
  private _actions$ = inject(Actions);
  private _router = inject(Router);
  private _loggerService = inject(LoggerService);

  @Action(LoginWithPassword)
  loginWithPassword(
    ctx: StateContext<AuthStateModel>,
    action: LoginWithPassword
  ): void | Observable<unknown> {
    const { login, lastRequestedRoute } = ctx.getState();
    const loginFormValues = login
      ? cloneDeep(login.loginFormValues)
      : cloneDeep(DEFAULT_AUTH_STATE.login.loginFormValues);

    if (action.data) {
      loginFormValues.model = { ...action.data, keepSession: false };
      loginFormValues.status = 'VALID';
    }

    if (loginFormValues.status !== 'VALID') {
      this._loggerService.debug('login form is invalid');
      return;
    }

    const { keepSession, ...data } = loginFormValues.model;
    return this._authService.loginWithPassword({ data }).pipe(
      tap({
        next: res => {
          if ('otp' in res.tokens) {
            const otp: Otp = {
              ...res.tokens.otp
            };
            const newState = immutable
              .wrap(ctx.getState())
              .set(`login.currentUserData`, res.user)
              .set(`login.otp`, otp)
              .value();
            ctx.setState(newState);
            // user should enter an otp code to login
            if (res.challenge?.type === '2fa') {
              this._router.navigateByUrl(ROUTES.loginOTP);
              return;
            }
            return;
          }

          if (
            !res.user.roles.includes(UserRole.ROLE_SUPER_ADMIN) &&
            !res.user.roles.includes(UserRole.ROLE_ADMIN)
          ) {
            this._toastService.open({
              type: 'error',
              content: "Vous n'avez pas le droits d'accéder à cette plateforme."
            });
            return;
          }

          setRememberMeStatus(true);
          ctx.patchState({
            currentUser: res.user,
            tokens: res.tokens
          });

          // check if password is a generated password and not changed by the user
          const passwordChange = res.user.passwordChanged;

          if (!passwordChange) {
            this._router.navigateByUrl(ROUTES.changePassword);
            return;
          }

          if (action.data?.redirectUrl) {
            this._router.navigateByUrl(action.data.redirectUrl);
            return;
          }

          if (lastRequestedRoute) {
            ctx.dispatch(new NavigateToLastRequestedRoute());
            return;
          }

          from(this._router.navigateByUrl(ROUTES.home)).pipe(
            finalize(() => {
              ctx.dispatch(new ResetRegisterState());
              ctx.dispatch(new ResetLoginState());
            })
          );
        },
        error: (error: HttpErrorResponse) => {
          const hasMultipleAccounts =
            error.status === 401 &&
            error?.error?.error === 'organization_ntd_required';

          if (hasMultipleAccounts) {
            this._toastService.open({
              content:
                "Vous n'avez pas le droits d'accéder à cette plateforme.",
              type: 'error'
            });
          } else {
            this._toastService.open({
              content:
                error?.error?.description ||
                error?.error?.detail ||
                'Une erreur inconnue est survenue, veuillez réessayer plus tard.',
              type: 'error'
            });
          }

          this._globalFacade.handleFormViolations(error);
        }
      })
    );
  }

  @Action(ResetOnboardingForms)
  resetOnboardingForms(ctx: StateContext<AuthStateModel>): void {
    ctx.patchState({
      onboarding: {
        locationFormValues: DEFAULT_AUTH_STATE.onboarding.locationFormValues,
        billingEquipmentFormValues:
          DEFAULT_AUTH_STATE.onboarding.billingEquipmentFormValues
      }
    });
  }

  @Action(LoginWithOneTimePassword)
  loginWithOneTimePassword(
    ctx: StateContext<AuthStateModel>,
    action: LoginWithOneTimePassword
  ): void | Observable<unknown> {
    const { login } = ctx.getState();
    const { loginFormValues, currentUserData } = login;

    if (loginFormValues.status !== 'VALID') {
      this._loggerService.debug('form is invalid');
      return;
    }

    const { username } = loginFormValues.model;
    return this._authService
      .loginWithOneTimePassword({
        data: { username, otpCode: action.data }
      })
      .pipe(
        tap({
          next: res => {
            setRememberMeStatus(true);
            ctx.patchState({
              currentUser: res.user,
              tokens: res.tokens as TokensResponse
            });

            from(this._router.navigateByUrl(ROUTES.home)).pipe(
              finalize(() => {
                ctx.dispatch(new ResetLoginState());
              })
            );
          },
          error: (error: HttpErrorResponse) => {
            if (error instanceof HttpErrorResponse) {
              if (error.status === 401) {
                this._toastService.open({
                  content: 'Le code OTP est incorrect.',
                  type: 'error'
                });

                return;
              }
            }

            this._globalFacade.handleFormViolations(error);
          }
        })
      );
  }

  @Action(LoginWithSocialNetwork)
  loginWithSocialNetwork(
    ctx: StateContext<AuthStateModel>,
    action: LoginWithSocialNetwork
  ): void | Observable<unknown> {
    const { lastRequestedRoute } = ctx.getState();
    const { name, tokenId, isRegistration } = action.data;

    return this._authService
      .loginWithSocialNetwork({ method: name, tokenId })
      .pipe(
        tap({
          next: res => {
            setRememberMeStatus(true);

            ctx.patchState({
              currentUser: res.user,
              tokens: res.tokens as TokensResponse
            });

            if (isRegistration) {
              this._toastService.open({
                content:
                  'Votre compte à été créé. Veuillez renseigner vos informations personnelles',
                type: 'success'
              });
              return;
            }

            if (lastRequestedRoute) {
              ctx.dispatch(new NavigateToLastRequestedRoute());
              return;
            }

            this._router.navigateByUrl(ROUTES.home);
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 0) {
              this._toastService.open({
                content: 'errors.internet-access',
                type: 'error'
              });
            } else {
              this._toastService.open({
                content: error.error.detail || `errors.default`,
                type: 'error'
              });
            }
            this._globalFacade.handleFormViolations(error);
          }
        })
      );
  }

  @Action(GetRegistrationOneTimePassword)
  getRegistrationOneTimePassword(
    ctx: StateContext<AuthStateModel>
  ): void | Observable<unknown> {
    const { registration } = ctx.getState();
    const { registrationFormValues } = registration;
    if (registrationFormValues.status !== 'VALID') {
      return;
    }
    const { email } = registrationFormValues.model;
    return this._authService.createOtp({ field: 'email', value: email! }).pipe(
      tap({
        next: otp => {
          const newState = immutable
            .wrap(ctx.getState())
            .set(`registration.otp`, {
              ...otp,
              field: 'email',
              receiver: email
            })
            .value();

          this._toastService.open({
            content:
              'Un code de confirmation a été envoyé sur votre adresse email',
            type: 'success'
          });

          ctx.setState(newState);
        },
        error: error => {
          const wrap = immutable.wrap(ctx.getState());
          const formGroupError = httpErrorToFormGroupError(error);
          wrap.set(
            'registration.registrationFormValues.httpErrors',
            formGroupError
          );
        }
      })
    );
  }

  @Action(ChangeUserPassword)
  changeUserPassword(
    ctx: StateContext<AuthStateModel>,
    action: ChangeUserPassword
  ): void | Observable<unknown> {
    return this._profileService.changePassword(action.data).pipe(
      tap({
        next: () => {
          ctx.dispatch(new Logout());
        },
        error: error => {
          this._globalFacade.handleFormViolations(error);
        }
      })
    );
  }

  // Registration Steps Management
  @Action(GoToNextRegistrationStep)
  goToNextStep(ctx: StateContext<AuthStateModel>): void {
    const state = ctx.getState();
    const { registration } = state;
    const { stepper } = registration;
    const wrap = immutable.wrap(state);
    wrap.set('registration.stepper', StepperState.goToNextStep(stepper));
    ctx.setState(wrap.value());
  }

  @Action(GoToPreviousRegistrationStep)
  goToPreviousStep(ctx: StateContext<AuthStateModel>): void {
    const state = ctx.getState();
    const { registration } = state;
    const { stepper } = registration;
    const wrap = immutable.wrap(state);
    wrap.set('registration.stepper', StepperState.goToPreviousStep(stepper));
    ctx.setState(wrap.value());
  }

  @Action(GoToRegistrationStep)
  goToStep(
    ctx: StateContext<AuthStateModel>,
    action: GoToRegistrationStep
  ): void {
    const state = ctx.getState();
    const { registration } = state;
    const { stepper } = registration;
    const wrap = immutable.wrap(state);
    wrap.set(
      'registration.stepper',
      StepperState.goToStep(stepper, action.payload)
    );
    ctx.setState(wrap.value());
  }

  @Action(RegisterUser)
  registerUser(
    ctx: StateContext<AuthStateModel>,
    action: RegisterUser
  ): void | Observable<unknown> {
    const { registration, identifiedCompany } = ctx.getState();
    const { registrationFormValues, emailConfirmationFormValues, isUserKnown } =
      registration;

    if (
      registrationFormValues.status !== 'VALID' ||
      emailConfirmationFormValues.status !== 'VALID' ||
      isUserKnown == undefined
    )
      return;

    const { phoneNumber, fullName, email } = registrationFormValues.model;

    const { password } = emailConfirmationFormValues.model;

    const { lastName, firstName } = getNamesFromFullName(fullName);

    const userData = removeNilProperties({
      email,
      password,
      lastName,
      firstName,
      phoneNumber: phoneNumber,
      organization: {
        name: identifiedCompany?.name,
        nif: identifiedCompany?.ncc,
        ntd: identifiedCompany?.ntd,
        activity: identifiedCompany?.natureActivity,
        taxSystem: identifiedCompany?.taxRegime,
        taxCenter: identifiedCompany?.accountRoleLabel,
        accountantRoleId: identifiedCompany?.postAccountantId,
        statusLabel: identifiedCompany?.statusLabel,
        taxDirection:
          identifiedCompany?.taxDirection || 'Direction de rattachement'
      }
    }) as UserCreationParams;

    const authRequest$ = defer<Observable<User | AuthResponse>>(() =>
      isUserKnown
        ? this._authService.registerWithPasswordAndOrganization(userData)
        : this._accountService.create(userData, action.data)
    );

    const passwordCheck$ = isUserKnown
      ? of(true)
      : this._accountService
          .checkUserPropertyValidity({ password, email })
          .pipe(
            catchError(error => {
              this._globalFacade.handleFormViolations(error, {
                password: true
              });
              return EMPTY;
            })
          );

    return passwordCheck$.pipe(
      switchMap(() =>
        authRequest$.pipe(
          tap({
            next: res => {
              if (this._isAuthResponse(res)) {
                ctx.patchState({
                  currentUser: res.user,
                  tokens: res.tokens as TokensResponse
                });

                this._router.navigateByUrl(ROUTES.home);
              } else if (password)
                ctx.dispatch(
                  new LoginWithPassword({ username: email, password: password })
                );
            },
            error: error => {
              const wrap = immutable.wrap(ctx.getState());

              const isNifAlreadyUsed =
                error?.error?.violations?.[0]?.propertyPath?.includes('nif');
              if (isNifAlreadyUsed) {
                this._eventService.emit({
                  type: 'GO_TO_PREVIOUS_STEP',
                  value: 0
                });
              }

              this._loggerService.debug('registration error', error);

              const formGroupError = httpErrorToFormGroupError(error);
              wrap.set(
                'registration.registrationFormValues.httpErrors',
                formGroupError
              );
            }
          }),
          takeUntil(this._actions$.pipe(ofActionDispatched(SetRouteData)))
        )
      )
    );
  }

  @Action(HydrateLoggedUserProfile)
  hydrateLoggedUserProfile(
    ctx: StateContext<AuthStateModel>
  ): void | Observable<unknown> {
    const { currentUser, tokens } = ctx.getState();
    if (!currentUser || !tokens) {
      return;
    }

    return this._profileService
      .read()
      .pipe(switchMap(profile => ctx.dispatch([new PatchLoggedUser(profile)])));
  }

  private _isAuthResponse(
    response: User | AuthResponse
  ): response is AuthResponse {
    return (response as AuthResponse).tokens !== undefined;
  }

  /*  @Action(CheckEmailExistence)
  checkEmailExistence(
    ctx: StateContext<AuthStateModel>,
    action: CheckEmailExistence
  ): Observable<unknown> {
    const email = (action.email || '').trim();
    if (!email) {
      ctx.patchState({ userExists: undefined });
      ctx.dispatch(new SetAuthMode('idle'));
      return of({});
    }

    // Example: /api/auth/check-email -> { exists: boolean }
    return this._accountService.checkUserPropertyValidity({email}).pipe(
      tap({
        next: (res) => {
          ctx.patchState({ userExists: false });
          ctx.dispatch(new SetAuthMode(res.exists ? 'login' : 'register'));
        },
        error: (error: HttpErrorResponse) => {
          // If your API uses 404 to mean "new user", treat like register
          if (error.status === 404) {
            ctx.patchState({ userExists: false });
            ctx.dispatch(new SetAuthMode('register'));
            return;
          }
          // Hard failure — keep UI stable and show a toast + keep mode idle
          this._toastService.open({
            content: error.error?.detail || `errors.default`,
            type: 'error'
          });
          ctx.patchState({ userExists: undefined });
          ctx.dispatch(new SetAuthMode('idle'));
        }
      })
    );
  } */

  @Action(GetResetOneTimePassword)
  getResetOneTimePassword(
    ctx: StateContext<AuthStateModel>
  ): void | Observable<unknown> {
    const { reset } = ctx.getState();
    const { requestFormValues } = reset;
    if (requestFormValues.status !== 'VALID') {
      return;
    }
    const { email } = requestFormValues.model;
    return this._authService.getResetOtp({ field: 'email', value: email }).pipe(
      tap({
        next: otp => {
          const newState = immutable
            .wrap(ctx.getState())
            .set(`reset.otp`, otp)
            .value();

          ctx.setState(newState);
        },
        error: error => this._globalFacade.handleFormViolations(error)
      })
    );
  }

  @Action(ResetUserPassword)
  resetUserPassword(
    ctx: StateContext<AuthStateModel>
  ): void | Observable<unknown> {
    const { reset } = ctx.getState();
    const { resetFormValues } = reset;
    if (resetFormValues.status !== 'VALID') {
      return;
    }
    return this._authService
      .resetPasswordWithToken(
        resetFormValues.model.password,
        resetFormValues.model.token
      )
      .pipe(
        tap({
          next: () => {
            ctx.patchState({ reset: DEFAULT_AUTH_STATE.reset });
            this._toastService.open({
              content:
                'Mot de passe réinitialisé avec succès. Veuillez vous reconnecter.',
              type: 'success'
            });
            this._router.navigateByUrl('/sign-in');
          },
          error: error => {
            const newState = immutable
              .wrap(ctx.getState())
              .update(`reset.otp`, otp => ({ ...otp, expiresAt: dayjs() }))
              .value();

            ctx.setState(newState);
            if (error.error?.violations) {
              this._globalFacade.handleFormViolations(error);
              return;
            }
            this._toastService.open({
              content:
                error.error?.detail || error.error?.title || `errors.default`,
              type: 'error'
            });
          }
        })
      );
  }

  @Action(RefreshAccessToken, { cancelUncompleted: false })
  refreshAccessToken(
    ctx: StateContext<AuthStateModel>
  ): void | Observable<unknown> {
    const { tokens } = ctx.getState();
    const { refreshToken } = tokens || {};

    return this._authService.refreshToken(refreshToken!.id).pipe(
      tap({
        next: res => {
          if ('otp' in res.tokens) {
            ctx.dispatch(new Logout());
            return;
          }
          ctx.patchState({ tokens: res.tokens });
        },
        error: () => {
          this._toastService.open({
            content: `Votre session à expirée. Veuillez vous reconnecter.`,
            type: 'error'
          });
          ctx.dispatch(new Logout());
        }
      })
    );
  }

  @Action(GetUserProfile, { cancelUncompleted: false })
  getUserProfile(
    ctx: StateContext<AuthStateModel>
  ): Observable<unknown> | void {
    return this._profileService.read().pipe(
      tap({
        next: res => {
          const { currentUser } = ctx.getState();
          ctx.patchState({ currentUser: { ...currentUser, ...res } });
        }
      })
    );
  }

  @Action(ResetLoginState)
  resetLoginState(ctx: StateContext<AuthStateModel>): void {
    ctx.patchState({ login: DEFAULT_AUTH_STATE.login });
  }

  @Action(ResetRegisterState)
  resetRegisterState(ctx: StateContext<AuthStateModel>): void {
    ctx.patchState({ registration: DEFAULT_AUTH_STATE.registration });
  }

  @Action(SetLastRequestedRoute)
  setLastRequestedRoute(
    ctx: StateContext<AuthStateModel>,
    action: SetLastRequestedRoute
  ): void | Observable<unknown> {
    ctx.patchState({
      lastRequestedRoute: action.data
    });
  }

  @Action(NavigateToLastRequestedRoute)
  navigateToLastRequestedRoute(
    ctx: StateContext<AuthStateModel>
  ): void | Observable<unknown> {
    const { lastRequestedRoute } = ctx.getState();
    if (!lastRequestedRoute) return;
    return from(this._router.navigateByUrl(lastRequestedRoute)).pipe(
      finalize(() => ctx.patchState({ lastRequestedRoute: undefined }))
    );
  }

  @Action(Logout, { cancelUncompleted: false })
  logout(ctx: StateContext<AuthStateModel>): Observable<unknown> | void {
    const { tokens, currentUser } = ctx.getState();

    if (!tokens || !currentUser) return;

    const logout$ = tokens
      ? this._authService
          .logout(tokens.accessToken)
          .pipe(catchError(() => of({})))
      : of({});

    return logout$.pipe(
      tap({
        next: () => {
          ctx.patchState({ currentUser: undefined, tokens: undefined });
          sessionStorage.clear();
          localStorage.clear();
          this._dialogService.closeAll();
          this._router.navigateByUrl(ROUTES.login);
        }
      })
    );
  }

  @Action(PatchLoggedUser)
  patchLoggedUser(
    ctx: StateContext<AuthStateModel>,
    action: PatchLoggedUser
  ): void | Observable<unknown> {
    const { currentUser } = ctx.getState();
    ctx.patchState({
      currentUser: { ...currentUser, ...(action.data as User) }
    });
  }

  @Action(PatchUserExistenceStatus)
  patchUserExistenceStatus(
    ctx: StateContext<AuthStateModel>,
    action: PatchUserExistenceStatus
  ): void | Observable<unknown> {
    const { registration } = ctx.getState();
    ctx.patchState({
      registration: { ...registration, isUserKnown: action.data }
    });
  }

  @Action(IdentifyUserCompany)
  identifyUserCompany(
    ctx: StateContext<AuthStateModel>,
    action: IdentifyUserCompany
  ): void | Observable<unknown> {
    return this._companyService.list(action.data).pipe(
      tap({
        next: res => {
          if (res.items.length === 0) {
            this._toastService.open({
              content:
                "Nous n'avons pas retrouvé d'entreprise correspondant à ces identifiants",
              type: 'error'
            });
            return;
          }

          ctx.patchState({
            identifiedCompany: res.items[0]
          });

          this._eventService.emit({ type: 'GO_TO_NEXT_STEP' });
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 0) {
            this._toastService.open({
              content: 'Veuillez vérifier votre accès à internet et réessayer.',
              type: 'error'
            });
            return;
          }
          this._toastService.open({
            content:
              'Une erreur inconnue est survenue, veuillez réessayer plus tard.',
            type: 'error'
          });
          this._globalFacade.handleFormViolations(error);
        }
      })
    );
  }

  // SELECTORS

  @Selector()
  static isUserKnown(state: AuthStateModel): boolean | undefined {
    return state.registration.isUserKnown;
  }

  @Selector()
  static identifiedCompany(state: AuthStateModel): Company | undefined {
    return state.identifiedCompany;
  }

  @Selector()
  static currentUser(state: AuthStateModel): User | undefined {
    return state.currentUser;
  }

  @Selector()
  static accessToken(state: AuthStateModel): AccessToken | undefined {
    return state.tokens?.accessToken;
  }

  @Selector()
  static refreshToken(state: AuthStateModel): RefreshToken | undefined {
    return state.tokens?.refreshToken;
  }

  @Selector()
  static locationFormValues(
    state: AuthStateModel
  ): StateFormModel<OrganisationFormValues> | undefined {
    return state.onboarding.locationFormValues;
  }

  @Selector()
  static billingEquipmentFormValues(state: AuthStateModel):
    | StateFormModel<{
        equipments: BillingEquipment[] | null;
        cashRegistersNumber: number | null;
      }>
    | undefined {
    return state.onboarding.billingEquipmentFormValues;
  }

  @Selector()
  static loginFormValues(
    state: AuthStateModel
  ): StateFormModel<LoginParams> | undefined {
    return state.login.loginFormValues;
  }

  @Selector()
  static loginCurrentUserData(
    state: AuthStateModel
  ): Partial<User> | undefined {
    return state.login.currentUserData;
  }

  @Selector()
  static challenge(state: AuthStateModel): Challenge | undefined {
    return state.login.challenge;
  }

  @Selector()
  static loginOtp(state: AuthStateModel): Otp | undefined {
    return state.login.otp;
  }

  @Selector()
  static loginOtpData(state: AuthStateModel): OtpData | undefined {
    const { otp, currentUserData } = state.login;
    if (otp && currentUserData) {
      return {
        ...otp,
        field: 'email',
        receiver: currentUserData.email
      } as OtpData;
    }
    return undefined;
  }

  @Selector()
  static isLoginFormValid(state: AuthStateModel): boolean {
    return state.login.loginFormValues.status === 'VALID';
  }

  @Selector()
  static registrationFormValues(
    state: AuthStateModel
  ): StateFormModel<RegistrationParams> | undefined {
    return state.registration.registrationFormValues;
  }

  @Selector()
  static registeredUserData(state: AuthStateModel): Partial<User> | undefined {
    return state.registration.registeredUserData;
  }

  @Selector()
  static registrationOtp(state: AuthStateModel): Otp | undefined {
    return state.registration.otp;
  }

  @Selector()
  static registrationStepper(state: AuthStateModel): StateStepper {
    return state.registration.stepper;
  }

  @Selector()
  static isRegistrationFormValid(state: AuthStateModel): boolean {
    return state.registration.registrationFormValues.status === 'VALID';
  }

  @Selector()
  static resetFormValues(
    state: AuthStateModel
  ): Pick<AuthStateModel, 'reset'>['reset']['resetFormValues'] {
    return state.reset.resetFormValues;
  }

  @Selector()
  static resetRequestFormValues(
    state: AuthStateModel
  ): Pick<AuthStateModel, 'reset'>['reset']['requestFormValues'] {
    return state.reset.requestFormValues;
  }

  @Selector()
  static resetUserData(
    state: AuthStateModel
  ): Pick<AuthStateModel, 'reset'>['reset']['resetUserData'] {
    return state.reset.resetUserData;
  }

  @Selector()
  static resetOtp(
    state: AuthStateModel
  ): Pick<AuthStateModel, 'reset'>['reset']['otp'] {
    return state.reset.otp;
  }

  @Selector()
  static resetOtpData(state: AuthStateModel): OtpData | undefined {
    const { otp, requestFormValues } = state.reset;
    if (otp && requestFormValues.model.email) {
      return {
        ...otp,
        field: 'email',
        receiver: requestFormValues.model.email
      } as OtpData;
    }
    return undefined;
  }

  @Selector()
  static isResetFormValid(state: AuthStateModel): boolean {
    return state.reset.resetFormValues.status === 'VALID';
  }

  @Selector()
  static isResetRequestFormValid(state: AuthStateModel): boolean {
    return state.reset.requestFormValues.status === 'VALID';
  }

  @Selector()
  static lastRequestedRoute(
    state: AuthStateModel
  ): Pick<AuthStateModel, 'lastRequestedRoute'>['lastRequestedRoute'] {
    return state.lastRequestedRoute;
  }
}
